/**
 * useRecentlyViewed — per-user localStorage list of recently opened
 * records, surfaced on the Dashboard so a one-click return to a
 * record-in-progress replaces a search (iter72.5).
 *
 * Deliberately client-only: no backend schema change, no round trip,
 * invalid entries (deleted records) get filtered on read. Keyed by
 * logged-in user id so multiple accounts on the same machine stay
 * separate.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const MAX_ITEMS = 5;
const STORAGE_PREFIX = 'elara.recents.';

function readList(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeList(userId, list) {
  if (!userId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + userId, JSON.stringify(list.slice(0, MAX_ITEMS)));
  } catch {
    /* quota exceeded — safe to ignore */
  }
}

/**
 * Hook consumed by callers that want to read the list (e.g. Dashboard
 * card). Returns a reactive array; mutations via `track()` update it
 * live in the current tab.
 */
export function useRecentlyViewed() {
  const { user } = useAuth();
  const userId = user?.id;
  const [items, setItems] = useState(() => readList(userId));

  // Re-read when the logged-in user changes (role switch, re-login).
  useEffect(() => {
    setItems(readList(userId));
  }, [userId]);

  // Keep tabs in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_PREFIX + userId) setItems(readList(userId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  const track = useCallback((entry) => {
    if (!userId || !entry || !entry.id || !entry.type) return;
    const now = Date.now();
    const next = [{ ...entry, ts: now }, ...items.filter(i => !(i.type === entry.type && i.id === entry.id))]
      .slice(0, MAX_ITEMS);
    writeList(userId, next);
    setItems(next);
  }, [items, userId]);

  const clear = useCallback(() => {
    writeList(userId, []);
    setItems([]);
  }, [userId]);

  return { items, track, clear };
}

/**
 * Thin fire-and-forget helper for callers that only need to *record*
 * a view and don't need the list. Writes straight to storage without
 * re-rendering the caller.
 */
export function trackRecentView(userId, entry) {
  if (!userId || !entry || !entry.id || !entry.type) return;
  const current = readList(userId);
  const next = [{ ...entry, ts: Date.now() }, ...current.filter(i => !(i.type === entry.type && i.id === entry.id))]
    .slice(0, MAX_ITEMS);
  writeList(userId, next);
  // Notify the same-tab listeners via a storage-like event so the
  // Dashboard card refreshes immediately.
  try {
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_PREFIX + userId }));
  } catch { /* some browsers don't allow constructing StorageEvent — ignore */ }
}
